import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Paper,
} from "@mui/material";
import {
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import moment from "moment";
import AdminApiModule from "../../../components/AdminApi/Api/api"; // Adjust path as needed

const TICKET_CREATED_BY_OPTIONS = [
  { value: "SUPER_ADMIN", label: "Super Admin" },
  { value: "CENTER", label: "Center" },
  { value: "CLIENT", label: "Client" },
  { value: "PUBLIC", label: "Public" },
  { value: "PUBLIC_APP", label: "Public App" },
  { value: "DEALER", label: "Dealer" },
  { value: "PUBLIC_WITH_CENTER_QR", label: "Public with Center QR" },
  { value: "PUBLIC_WITH_CLIENT_QR", label: "Public with Client QR" },
  { value: "PUBLIC_WITH_DELAR_QR", label: "Public with Dealer QR" },
  { value: "PUBLIC_WITH_SOCIETY_QR", label: "Public with Society QR" },
  { value: "IMPORT_VIA_EXCEL", label: "Import via Excel" },
];

const TIME_PREFERENCE_OPTIONS = [
  { value: "IMMEDIATELY", label: "Immediately" },
  { value: "WITHIN_24_HOURS", label: "Within 24 Hours" },
  { value: "WITHIN_48_HOURS", label: "Within 48 Hours" },
  { value: "SPECIFIC_DATE_AND_TIME", label: "Specific Date & Time" },
];

const TICKET_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "ACCEPTED", label: "Accepted" },
  { value: "ON_HOLD", label: "On Hold" },
  { value: "CLOSED", label: "Closed" },
  { value: "VERIFIED", label: "Verified" },
  { value: "REJECTED", label: "Rejected" },
  { value: "RE_OPEN", label: "Re-Open" },
  { value: "CANCEL", label: "Cancel" },
];

const BROADCAST_STATUS_OPTIONS = [
  { value: "BROADCASTED", label: "Broadcasted" },
  { value: "ACCEPTED_AFTER_BROADCAST", label: "Accepted After Broadcast" },
  { value: "MATCHED_IN_SAME_CENTER", label: "Matched in Same Center" },
  { value: "MATCHED_IN_SAME_CLIENT", label: "Matched in Same Client" },
  {
    value: "WARRENTY_REGISTER_FOR_CLIENT",
    label: "Warranty Register for Client",
  },
  { value: "NO_MATCH_FOUND", label: "No Match Found" },
  { value: "NO_CLIENT_MATCH_FOUND", label: "No Client Match Found" },
  {
    value: "CLIENT_WALLET_NOT_SUFFICIENT",
    label: "Client Wallet Not Sufficient",
  },
  {
    value: " ACCEPTED_TECHNICIAN_NOT_ASSIGNED",
    label: "Accepted Technician Not Assigned",
  },
];

// Load filters from localStorage with unique key
const loadFiltersFromStorage = () => {
  try {
    const stored = localStorage.getItem("ticketFiltersStandard");
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Error loading filters from storage:", error);
  }
  return null;
};

// Save filters to localStorage with unique key
const saveFiltersToStorage = (filters) => {
  try {
    localStorage.setItem("ticketFiltersStandard", JSON.stringify(filters));
  } catch (error) {
    console.error("Error saving filters to storage:", error);
  }
};

// Clear filters from localStorage with unique key
const clearFiltersFromStorage = () => {
  try {
    localStorage.removeItem("ticketFiltersStandard");
  } catch (error) {
    console.error("Error clearing filters from storage:", error);
  }
};

const TicketFilters = ({
  onFilterChange,
  onClearFilters,
  isLoading = false,
}) => {
  const defaultFilters = {
    ticket_id: "",
    ticket_status: "",
    broadcast_status: "",
    time_preference_type: "",
    pincode: "",
    ticket_created_by: "",
    is_paid_by_public: "",
    is_paid_by_technician: "",
    is_technician_assigned: "",
    name: "",
    phone_number: "",
    city: "",
    client_id: "", // Added client_id filter
    service_provided_client_id: "", // Added client request ID filter
  };

  const [filters, setFilters] = useState({
    ...defaultFilters,
    ...getDefaultDateRange(),
  });
  const [expanded, setExpanded] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  function getDefaultDateRange() {
    return {
      start_date: moment().subtract(1, "week").format("YYYY-MM-DD"),
      end_date: moment().format("YYYY-MM-DD"),
    };
  }

  // Load clients for the client filter
  useEffect(() => {
    const getAllClients = () => {
      setClientsLoading(true);
      new AdminApiModule()
        .getAllClients()
        .then((res) => {
          let parsedData = [];
          if (res && res.data && res.data.length > 0) {
            parsedData = res.data;
          }

          const clientOptions = parsedData.map((client) => ({
            value: client.client_id,
            label: client.client_name || client.client_id,
          }));
          setClients(clientOptions);
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setClientsLoading(false);
        });
    };

    getAllClients();
  }, []);

  // Load filters from localStorage on component mount
  useEffect(() => {
    const storedFilters = loadFiltersFromStorage();
    if (storedFilters) {
      setFilters(storedFilters);
      onFilterChange(storedFilters);
    } else {
      handleApplyFilters();
    }
  }, []);

  const handleFilterChange = (field, value) => {
    const newFilters = {
      ...filters,
      [field]: value,
    };
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([_, value]) => value !== "" && value !== null && value !== undefined
      )
    );

    saveFiltersToStorage(cleanFilters);
    onFilterChange(cleanFilters);
    setExpanded(false);
  };

  const handleClearFilters = () => {
    clearFiltersFromStorage();
    const clearedFilters = {
      ...defaultFilters,
    };
    setFilters(clearedFilters);
    onClearFilters(clearedFilters);
  };

  const getActiveFilterCount = () => {
    return Object.entries(filters).filter(
      ([key, value]) => value !== "" && value !== null && value !== undefined
    ).length;
  };

  const activeFilterCount = getActiveFilterCount();

  const getFilterDisplayValue = (key, value) => {
    if (!value) return "";

    switch (key) {
      case "ticket_status":
        const statusOption = TICKET_STATUS_OPTIONS.find(
          (opt) => opt.value === value
        );
        return statusOption ? statusOption.label : value;

      case "broadcast_status":
        const broadcastOption = BROADCAST_STATUS_OPTIONS.find(
          (opt) => opt.value === value
        );
        return broadcastOption ? broadcastOption.label : value;

      case "ticket_created_by":
        const createdByOption = TICKET_CREATED_BY_OPTIONS.find(
          (opt) => opt.value === value
        );
        return createdByOption ? createdByOption.label : value;

      case "time_preference_type":
        const timeOption = TIME_PREFERENCE_OPTIONS.find(
          (opt) => opt.value === value
        );
        return timeOption ? timeOption.label : value;

      case "is_paid_by_public":
        return value === "true" ? "Paid by Public: Yes" : "Paid by Public: No";

      case "is_paid_by_technician":
        return value === "true"
          ? "Paid by Technician: Yes"
          : "Paid by Technician: No";

      case "is_technician_assigned":
        return value === "true"
          ? "Technician Assigned: Yes"
          : "Technician Assigned: No";

      case "client_id":
        const clientOption = clients.find((opt) => opt.value === value);
        return clientOption
          ? `Client: ${clientOption.label}`
          : `Client: ${value}`;

      case "start_date":
        return `Start Date: ${moment(value).format("MMM D, YYYY")}`;

      case "end_date":
        return `End Date: ${moment(value).format("MMM D, YYYY")}`;

      default:
        const fieldName = key.replace(/_/g, " ");
        return `${fieldName}: ${value}`;
    }
  };

  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) => value !== "" && value !== null && value !== undefined
  );

  const handleRemoveFilter = (key) => {
    handleFilterChange(key, "");
  };

  return (
    <Card className="mb-6 shadow-lg border-0 rounded-xl">
      <CardContent>
        <Accordion
          expanded={expanded}
          onChange={(_, isExpanded) => setExpanded(isExpanded)}
          className="shadow-none border-0"
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-all duration-200"
          >
            <Box className="flex items-center justify-between w-full">
              <Box className="flex items-center space-x-3">
                <FilterIcon className="text-indigo-600" />
                <Typography
                  variant="h6"
                  className="font-semibold text-gray-800"
                >
                  Filters
                </Typography>
                {activeFilterCount > 0 && (
                  <Chip
                    label={`${activeFilterCount} active`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
              <Box className="flex items-center space-x-2">
                {activeFilterCount > 0 && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearFilters();
                    }}
                    className="text-red-500 hover:bg-red-50"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails className="bg-white pt-6">
            {/* Date Range Row */}
            <Paper className="p-4 mb-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border">
              <Typography
                variant="subtitle1"
                className="font-semibold text-gray-700 mb-3"
              >
                Date Range
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Start Date"
                    value={filters.start_date || ""}
                    onChange={(e) =>
                      handleFilterChange("start_date", e.target.value)
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    className="bg-white"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="End Date"
                    value={filters.end_date || ""}
                    onChange={(e) =>
                      handleFilterChange("end_date", e.target.value)
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    className="bg-white"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Quick Search Row */}
            <Paper className="p-4 mb-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border">
              <Typography
                variant="subtitle1"
                className="font-semibold text-gray-700 mb-3"
              >
                Quick Search
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Request ID"
                    value={filters.ticket_id}
                    onChange={(e) =>
                      handleFilterChange("ticket_id", e.target.value)
                    }
                    placeholder="Enter request ID"
                    className="bg-white"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Customer Name"
                    value={filters.name}
                    onChange={(e) => handleFilterChange("name", e.target.value)}
                    placeholder="Enter customer name"
                    className="bg-white"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone Number"
                    value={filters.phone_number}
                    onChange={(e) =>
                      handleFilterChange("phone_number", e.target.value)
                    }
                    placeholder="Enter phone number"
                    className="bg-white"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Pincode"
                    value={filters.pincode}
                    onChange={(e) =>
                      handleFilterChange("pincode", e.target.value)
                    }
                    placeholder="Enter pincode"
                    className="bg-white"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Client Filters */}
            <Paper className="p-4 mb-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border">
              <Typography
                variant="subtitle1"
                className="font-semibold text-gray-700 mb-3"
              >
                Client Filters
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Client</InputLabel>
                    <Select
                      value={filters.client_id || ""}
                      label="Client"
                      onChange={(e) =>
                        handleFilterChange("client_id", e.target.value)
                      }
                      disabled={clientsLoading}
                      sx={{ minWidth: 220 }}
                    >
                      <MenuItem value="">All Clients</MenuItem>
                      {clients.map((client) => (
                        <MenuItem key={client.value} value={client.value}>
                          {client.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Client Request ID"
                    value={filters.service_provided_client_id || ""}
                    onChange={(e) =>
                      handleFilterChange(
                        "service_provided_client_id",
                        e.target.value
                      )
                    }
                    placeholder="Enter client request ID"
                    className="bg-white"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Advanced Filters */}
            <Typography
              variant="subtitle1"
              className="font-semibold text-gray-700 mb-4"
            >
              Advanced Filters
            </Typography>
            <Grid container spacing={3}>
              {/* Status Filters */}
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Request Status</InputLabel>
                  <Select
                    value={filters.ticket_status}
                    label="Request Status"
                    onChange={(e) =>
                      handleFilterChange("ticket_status", e.target.value)
                    }
                  >
                    <MenuItem value="">All Status</MenuItem>
                    {TICKET_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Broadcast Status</InputLabel>
                  <Select
                    value={filters.broadcast_status}
                    label="Broadcast Status"
                    onChange={(e) =>
                      handleFilterChange("broadcast_status", e.target.value)
                    }
                  >
                    <MenuItem value="">All Broadcast Status</MenuItem>
                    {BROADCAST_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Created By */}
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Created By</InputLabel>
                  <Select
                    value={filters.ticket_created_by}
                    label="Created By"
                    onChange={(e) =>
                      handleFilterChange("ticket_created_by", e.target.value)
                    }
                  >
                    <MenuItem value="">All Sources</MenuItem>
                    {TICKET_CREATED_BY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Time Preference */}
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Time Preference</InputLabel>
                  <Select
                    value={filters.time_preference_type}
                    label="Time Preference"
                    onChange={(e) =>
                      handleFilterChange("time_preference_type", e.target.value)
                    }
                  >
                    <MenuItem value="">All Preferences</MenuItem>
                    {TIME_PREFERENCE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Payment Filters */}
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Paid by Public</InputLabel>
                  <Select
                    value={filters.is_paid_by_public}
                    label="Paid by Public"
                    onChange={(e) =>
                      handleFilterChange("is_paid_by_public", e.target.value)
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Paid by Technician</InputLabel>
                  <Select
                    value={filters.is_paid_by_technician}
                    label="Paid by Technician"
                    onChange={(e) =>
                      handleFilterChange(
                        "is_paid_by_technician",
                        e.target.value
                      )
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Assignment Filter */}
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Technician Assigned</InputLabel>
                  <Select
                    value={filters.is_technician_assigned}
                    label="Technician Assigned"
                    onChange={(e) =>
                      handleFilterChange(
                        "is_technician_assigned",
                        e.target.value
                      )
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* City Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  value={filters.city}
                  onChange={(e) => handleFilterChange("city", e.target.value)}
                  placeholder="Enter city"
                />
              </Grid>
            </Grid>

            {/* Active Filters Display */}
            {hasActiveFilters && (
              <Box className="mt-6 pt-4 border-t">
                <Typography
                  variant="body2"
                  className="text-gray-600 mb-2 font-medium"
                >
                  Active filters:
                </Typography>
                <Box className="flex flex-wrap gap-2">
                  {Object.entries(filters).map(([key, value]) => {
                    if (!value || value === "") return null;

                    const displayValue = getFilterDisplayValue(key, value);
                    if (!displayValue) return null;

                    return (
                      <Chip
                        key={key}
                        label={displayValue}
                        size="small"
                        onDelete={() => handleRemoveFilter(key)}
                        color="primary"
                        variant="outlined"
                        className="transition-all duration-200 hover:shadow-md"
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Action Buttons */}
            <Box className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                className="border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50"
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                startIcon={<SearchIcon />}
                disabled={isLoading}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg"
              >
                {isLoading ? "Applying..." : "Apply Filters"}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default TicketFilters;
